import styled from 'styled-components';

/**
 * Self-contained, dark-forced recreation of the Weather Center design.
 * All design tokens + component CSS are scoped under this root via
 * styled-components so nothing leaks into the rest of the admin shell.
 * Light-theme rules from the prototype are intentionally dropped — the
 * static build ships dark only (per request).
 */
export const WCRoot = styled.div`
  /* ---------- Brand ---------- */
  --brand-primary: #fa4616;
  --brand-primary-hover: #e03d12;
  --brand-primary-light: #ff6a3d;
  --brand-secondary: #00001f;
  --brand-blue-black: #000033;

  /* ---------- Surfaces (dark) ---------- */
  --bg-primary: #0f0f23;
  --bg-shell: #0a0a18;
  --bg-secondary: #1a1a2e;
  --bg-tertiary: #14142a;
  --bg-modal: #0f0f23;
  --bg-inset: #14142a;
  --bg-card: #1a1a2e;
  --bg-overlay: rgba(0, 0, 0, 0.7);

  --text-primary: #ffffff;
  --text-secondary: rgba(255, 255, 255, 0.6);
  --text-tertiary: rgba(255, 255, 255, 0.45);
  --text-inverse: #ffffff;
  --text-disabled: rgba(255, 255, 255, 0.35);

  --border-default: rgba(255, 255, 255, 0.11);
  --border-muted: rgba(255, 255, 255, 0.05);
  --border-strong: rgba(255, 255, 255, 0.18);

  /* ---------- Semantic ---------- */
  --success: #22c55e;
  --error: #ed4337;
  --warning: #ff9800;
  --info: #2196f3;
  --success-bg: rgba(34, 197, 94, 0.2);
  --error-bg: rgba(237, 67, 55, 0.2);
  --warning-bg: rgba(255, 152, 0, 0.2);
  --info-bg: rgba(33, 150, 243, 0.2);

  --accent-premium: #8b5cf6;
  --accent-new: #06b6d4;
  --accent-slate: #475569;

  /* ---------- Spacing ---------- */
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
  --space-xxl: 48px;

  /* ---------- Radius ---------- */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-input: 6px;
  --radius-lg: 12px;
  --radius-card: 14px;
  --radius-xl: 16px;
  --radius-pill: 20px;
  --radius-round: 9999px;

  /* ---------- Shadows (dark) ---------- */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 4px 16px rgba(0, 0, 0, 0.5);
  --shadow-xl: 0 8px 32px rgba(0, 0, 0, 0.6);
  --shadow-glow: 0 0 20px rgba(250, 70, 22, 0.2);
  --shadow-focus-ring: 0 0 0 3px rgba(250, 70, 22, 0.1);

  /* ---------- Type ---------- */
  --font-system: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  --font-display: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  --font-mono: ui-monospace, 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  --fs-xs: 13px;
  --fs-sm: 14px;
  --fs-md: 16px;
  --fs-lg: 18px;
  --fs-xl: 22px;
  --fs-xxl: 26px;
  --fs-display: 34px;
  --fw-regular: 400;
  --fw-medium: 500;
  --fw-semibold: 600;
  --fw-bold: 700;
  --fw-heavy: 800;
  --tracking-label: 1.5px;
  --tracking-tight: -0.2px;
  --tracking-caps: 0.5px;
  --ease-out: cubic-bezier(0.2, 0.8, 0.2, 1);
  --dur-fast: 150ms;
  --dur-base: 250ms;
  --dur-slow: 300ms;

  /* ---------- Legacy aliases ---------- */
  --navy: var(--bg-primary);
  --navy-light: var(--bg-secondary);
  --navy-card: var(--bg-secondary);
  --orange: var(--brand-primary);
  --orange-hover: var(--brand-primary-hover);
  --white: var(--text-primary);
  --text-muted: var(--text-tertiary);
  --border: var(--border-default);
  --border-hover: var(--border-strong);
  --card-bg: var(--bg-secondary);
  --card-bg-hover: var(--bg-tertiary);

  /* ---------- Elevation / overlay tokens (dark) ---------- */
  --surface-1: rgba(255, 255, 255, 0.025);
  --surface-2: rgba(255, 255, 255, 0.045);
  --surface-3: rgba(255, 255, 255, 0.075);
  --surface-4: rgba(255, 255, 255, 0.16);
  --hairline: rgba(255, 255, 255, 0.1);
  --dash-line: rgba(255, 255, 255, 0.1);
  --scrollbar-thumb: rgba(255, 255, 255, 0.12);
  --scrollbar-thumb-hover: rgba(255, 255, 255, 0.2);
  --map-surface: var(--bg-secondary);
  --map-fill-1: var(--surface-2);
  --map-fill-2: rgba(255, 255, 255, 0.15);
  --ramp-100: #ffffff;
  --ramp-55: rgba(255, 255, 255, 0.55);
  --ramp-30: rgba(255, 255, 255, 0.3);
  --ramp-15: rgba(255, 255, 255, 0.15);

  /* ---------- Weather ramps ---------- */
  --rain-none: var(--surface-2);
  --rain-light: #4fc3f7;
  --rain-moderate: var(--info);
  --rain-heavy: #1565c0;
  --rain-extreme: #7b1fa2;
  --alert-extreme: #ff1744;
  --alert-severe: #ff6d00;
  --alert-moderate: #ffd600;
  --alert-minor: #69f0ae;
  --lightning-clear: var(--success);
  --lightning-caution: var(--warning);
  --lightning-danger: #ff1744;
  --temp-hot: #ff5722;
  --temp-warm: var(--warning);
  --temp-mild: var(--success);
  --temp-cool: var(--info);
  --temp-cold: #7c4dff;

  /* ============================================================ */
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-system);
  font-size: var(--fs-md);
  line-height: 1.5;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Haloed orange icon tiles */
  .header-icon-tile,
  .modal-title-icon-tile,
  .card-title-tip .tip-ico,
  .card-title-icon,
  .expand-btn,
  .radar-hint:not(.on-alert) .rh-icon,
  .phrases-trigger > svg:not(.phrases-chevron),
  .day-summary-icon {
    background: rgba(250, 70, 22, 0.16);
    border: 1px solid rgba(250, 70, 22, 0.34);
    box-shadow: 0 0 0 2px rgba(250, 70, 22, 0.08);
    box-sizing: border-box;
  }

  /* ---------- Layout ---------- */
  .dashboard {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px 20px 60px;
  }
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    padding: 18px 22px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
  }
  .dashboard-header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }
  .header-icon-tile {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: var(--shadow-glow);
  }
  .header-icon-tile svg {
    width: 34px;
    height: 34px;
    color: var(--orange);
    stroke-width: 2.2;
  }
  .dashboard-header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .dashboard-header h1 {
    font-weight: var(--fw-heavy);
    font-size: 38px;
    letter-spacing: -0.8px;
    line-height: 1.05;
    color: var(--text-primary);
    text-transform: none;
  }
  .dashboard-header h1.dash-title {
    display: flex;
    align-items: flex-start;
    gap: 17px;
  }
  .pitpay-wordmark {
    height: 56px;
    width: auto;
    display: block;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .title-stack {
    display: inline-flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .title-word {
    line-height: 1.02;
  }
  .venue-name {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .venue-pen {
    display: inline-flex;
    align-items: center;
    color: var(--orange);
    flex-shrink: 0;
  }
  .venue-pen svg {
    width: 15px;
    height: 15px;
  }
  .dashboard-header .venue-name {
    font-size: 15px;
    color: var(--text-secondary);
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
  }
  .header-icon {
    display: none;
  }
  .xweather-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
  .xweather-badge img {
    width: 16px;
    height: 16px;
    border-radius: 2px;
  }
  .xweather-badge-sm {
    margin-top: 6px;
    padding: 3px 9px;
    font-size: 9.5px;
    letter-spacing: 0.4px;
    border-radius: 14px;
    align-self: flex-start;
  }
  .venue-name .xweather-badge-sm {
    margin-top: 0;
    margin-left: 6px;
    align-self: center;
  }
  .presented-by-xw {
    margin-top: 6px;
    align-self: flex-end;
    font-size: 8.5px;
    padding: 2px 7px;
    letter-spacing: 0.3px;
  }
  .header-attribution {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;
  }
  .theme-toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 12px 6px 9px;
    border-radius: var(--radius-pill);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-secondary);
    font-family: var(--font-system);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out);
  }
  .theme-toggle:hover {
    background: var(--surface-3);
    color: var(--text-primary);
    border-color: var(--border-strong);
  }
  .theme-toggle svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
  .theme-toggle .tt-sun {
    display: inline;
    color: var(--brand-primary);
  }
  .theme-toggle .tt-moon {
    display: none;
  }
  .presented-by {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 7px;
  }
  .presented-by-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: var(--text-muted);
    white-space: nowrap;
  }
  .presented-by-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    padding: 9px 18px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.28);
  }
  .presented-by-logo img {
    height: 42px;
    width: auto;
    display: block;
  }

  /* ---------- Cards ---------- */
  .card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    padding: var(--space-md);
    transition: border-color var(--dur-base) var(--ease-out);
  }
  .card:hover {
    border-color: var(--border-hover);
  }
  .card-title {
    font-weight: var(--fw-heavy);
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: var(--tracking-label);
    color: var(--text-primary);
    margin-bottom: 18px;
  }
  .card-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .card-title-row .card-title {
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .card-title-icon {
    width: 34px;
    height: 34px;
    padding: 8px;
    border-radius: 10px;
    color: var(--brand-primary);
    flex-shrink: 0;
    stroke-width: 2.2;
  }
  .card-title-tip {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 17px;
    color: var(--brand-primary);
    cursor: pointer;
    flex-shrink: 0;
    margin-left: 1px;
    transition: color 0.2s ease;
  }
  .card-title-tip .tip-q {
    width: 17px;
    height: 17px;
    display: block;
  }
  .card-title-tip:hover {
    color: var(--brand-primary-hover, var(--brand-primary));
  }
  .card-title-tip .tip-text {
    display: none;
    position: absolute;
    top: calc(100% + 9px);
    left: -10px;
    align-items: flex-start;
    gap: 11px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 12.5px;
    font-weight: 400;
    color: var(--text-secondary);
    white-space: normal;
    width: 256px;
    line-height: 1.5;
    letter-spacing: 0;
    text-transform: none;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.55);
    z-index: 100;
    pointer-events: none;
  }
  .card-title-tip .tip-ico {
    flex-shrink: 0;
    margin-top: 1px;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .card-title-tip .tip-ico svg {
    width: 15px;
    height: 15px;
    color: #fa4616;
    stroke: #fa4616;
  }
  .card-title-tip .tip-text::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: var(--arrow-left, 16px);
    border: 6px solid transparent;
    border-bottom-color: var(--bg-secondary);
  }
  .card-title-tip:hover .tip-text {
    display: flex;
  }
  .ct-label {
    display: inline;
    line-height: 1.25;
    min-width: 0;
  }
  .ct-label .card-title-tip {
    vertical-align: -3px;
    margin-left: 4px;
  }
  .card-title-tip .tip-text.flip-left {
    left: auto;
    right: -10px;
  }
  .card-title-tip .tip-text.flip-left::after {
    left: auto;
    right: 16px;
  }
  .tip-fixed::after {
    display: none !important;
  }
  .expand-btn {
    color: var(--brand-primary);
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--dur-base) var(--ease-out);
    flex-shrink: 0;
    font-weight: var(--fw-bold);
  }
  .expand-btn:hover {
    background: rgba(250, 70, 22, 0.2);
    border-color: rgba(250, 70, 22, 0.5);
    color: var(--brand-primary-hover);
    box-shadow: var(--shadow-glow);
  }
  .expand-btn svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.8;
  }

  @media (min-width: 860px) {
    .card {
      padding: var(--space-lg);
    }
  }

  /* ---------- Alert banner ---------- */
  .alert-banner {
    background: var(--brand-primary);
    border: none;
    border-radius: var(--radius-lg);
    padding: 20px 24px;
    margin-bottom: 20px;
    cursor: pointer;
    transition: background var(--dur-base) var(--ease-out);
    color: var(--text-inverse);
    box-shadow: var(--shadow-glow);
  }
  .alert-banner:hover {
    background: var(--brand-primary-hover);
  }
  .alert-banner-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .alert-icon {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
  }
  .alert-type {
    font-weight: var(--fw-heavy);
    font-size: 18px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: white;
  }
  .alert-summary {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .alert-until-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 0, 0, 0.28);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: white;
    font-weight: 800;
    letter-spacing: 0.4px;
    font-variant-numeric: tabular-nums;
    padding: 5px 10px 5px 9px;
    border-radius: 999px;
    font-size: 13px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .alert-until-pill svg {
    width: 12px;
    height: 12px;
    stroke: white;
    stroke-width: 2.5;
    flex-shrink: 0;
  }
  .alert-until-pill .until-label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 700;
    letter-spacing: 0.8px;
  }
  .alert-county {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 500;
  }
  .alert-chevron {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    transition: transform 0.25s ease;
    fill: rgba(255, 255, 255, 0.7);
  }
  .alert-banner.expanded .alert-chevron {
    transform: rotate(180deg);
  }
  .alert-detail {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, padding 0.3s ease;
  }
  .alert-banner.expanded .alert-detail {
    /* Generous cap so long advisories (lots of body text + the timeline) never
       clip on narrow mobile screens; the element still sizes to its content. */
    max-height: 2000px;
    padding-top: 16px;
  }
  .alert-detail .alert-detail-grid {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.28);
    padding-top: 16px;
  }
  .alert-detail p {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.95);
  }
  .alert-timeline {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    background: rgba(0, 0, 0, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 12px;
    padding: 16px 24px;
  }
  .atl-item {
    display: flex;
    align-items: center;
    gap: 13px;
    flex: 0 0 auto;
  }
  .atl-conn {
    flex: 0 0 auto;
    width: 44px;
    min-width: 0;
    height: 2px;
    background: rgba(255, 255, 255, 0.28);
    margin: 0 16px;
  }
  .atl-node {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.12);
    border: 1.5px solid rgba(255, 255, 255, 0.45);
    color: #fff;
  }
  .atl-node svg {
    width: 16px;
    height: 16px;
  }
  .atl-item.is-done .atl-node {
    background: #fff;
    border-color: #fff;
    color: var(--brand-primary);
  }
  .atl-item.is-active .atl-node {
    background: #fff;
    border-color: #fff;
    color: var(--brand-primary);
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.22);
  }
  .atl-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
  }
  .atl-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.7);
  }
  .atl-time {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  @media (max-width: 720px) {
    .alert-timeline {
      flex-direction: column;
      align-items: stretch;
      gap: 0;
    }
    .atl-conn {
      width: 2px;
      height: 18px;
      min-width: 0;
      margin: 4px 0 4px 15px;
      flex: 0 0 auto;
    }
  }

  /* ---------- Row 2: conditions + radar ---------- */
  /* Row 2 shares the SAME fluid 4-column grid as .precip-row below, so their
     column boundaries line up at any width. Current Conditions takes column 1
     (a slightly heavier 1.2fr so the left column is a bit wider as a % of the
     page); the radar card spans columns 2–4 to match the three cards beneath. */
  .row-top {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
  .row-top .radar-hub {
    grid-column: 2 / -1;
  }
  .temp-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 6px;
  }
  .weather-icon-wrap {
    width: 96px;
    height: 96px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .weather-icon-wrap img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .temp-main {
    font-weight: var(--fw-heavy);
    font-size: 84px;
    line-height: 1;
    letter-spacing: -3px;
    font-variant-numeric: tabular-nums;
  }
  .temp-main sup {
    font-size: 32px;
    font-weight: 600;
    vertical-align: super;
    letter-spacing: 0;
  }
  .temp-description {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 4px;
  }
  .temp-feels {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-muted);
    margin-bottom: 20px;
  }
  .conditions-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
  }
  .condition-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid var(--border);
  }
  .condition-item:nth-child(odd) {
    padding-right: 14px;
    border-right: 1px solid var(--border);
  }
  .condition-item:nth-child(even) {
    padding-left: 14px;
  }
  .condition-item:nth-last-child(-n + 2) {
    border-bottom: none;
  }
  .condition-label {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 700;
  }
  .condition-value {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-primary);
    white-space: nowrap;
  }
  .sun-row {
    display: flex;
    justify-content: space-between;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }
  .sun-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sun-icon {
    width: 18px;
    height: 18px;
    fill: var(--orange);
  }
  .sun-label {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 700;
  }
  .sun-time {
    font-size: 18px;
    font-weight: 700;
  }

  /* ---------- Radar hub ---------- */
  .radar-hub {
    display: flex;
    flex-direction: column;
  }
  .radar-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }
  .radar-tab {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    padding: 6px 12px;
    border-radius: 16px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
  }
  .radar-tab.active {
    background: rgba(250, 70, 22, 0.15);
    border-color: rgba(250, 70, 22, 0.4);
    color: var(--orange);
  }
  .radar-tab:hover {
    border-color: var(--border-hover);
    color: var(--text-secondary);
  }
  .radar-tab.active:hover {
    background: rgba(250, 70, 22, 0.22);
    border-color: rgba(250, 70, 22, 0.5);
    color: var(--orange);
  }
  .radar-tab-panel {
    display: none;
  }
  .radar-tab-panel.active {
    display: block;
  }
  .layer-toggles {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .layer-chip {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    padding: 7px 14px;
    border-radius: 16px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
  }
  .layer-chip.active {
    background: rgba(33, 150, 243, 0.12);
    border-color: rgba(33, 150, 243, 0.35);
    color: var(--rain-moderate);
  }
  .layer-chip:hover {
    border-color: var(--border-hover);
    color: var(--text-secondary);
  }
  .radar-map-wrap {
    position: relative;
    flex: 1;
    min-height: 340px;
    background: var(--map-surface);
    border-radius: 8px;
    margin-bottom: 14px;
    overflow: hidden;
    background-image: radial-gradient(ellipse at 60% 40%, rgba(34, 197, 94, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse at 30% 70%, rgba(33, 150, 243, 0.08) 0%, transparent 40%),
      radial-gradient(ellipse at 75% 25%, rgba(239, 68, 68, 0.06) 0%, transparent 30%);
  }
  .radar-leaflet {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background: var(--map-surface);
  }
  .radar-leaflet .leaflet-tile-pane {
    /* let the dark base + radar blend on the dark card surface */
    mix-blend-mode: normal;
  }
  .radar-precip-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .radar-precip-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(12px);
    opacity: 0.6;
    transition: width 0.55s cubic-bezier(0.4, 0, 0.2, 1),
      height 0.55s cubic-bezier(0.4, 0, 0.2, 1), top 0.55s cubic-bezier(0.4, 0, 0.2, 1),
      left 0.55s cubic-bezier(0.4, 0, 0.2, 1), background 0.55s ease;
  }
  .radar-timeline {
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 14px;
  }
  .radar-play-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(250, 70, 22, 0.15);
    border: 1.5px solid rgba(250, 70, 22, 0.4);
    color: var(--orange);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
    padding: 0;
  }
  .radar-play-btn:hover {
    background: rgba(250, 70, 22, 0.25);
    border-color: rgba(250, 70, 22, 0.6);
    box-shadow: var(--shadow-glow);
  }
  .radar-play-btn svg {
    width: 14px;
    height: 14px;
    fill: var(--orange);
  }
  .radar-timeline-track {
    flex: 1;
    min-width: 0;
    position: relative;
    height: 50px;
    padding-top: 14px;
    display: flex;
    align-items: center;
  }
  .radar-timeline-line {
    position: absolute;
    left: 4px;
    right: 4px;
    top: 50%;
    height: 2px;
    background: var(--surface-3);
    transform: translateY(-50%);
  }
  .radar-timeline-line::before {
    content: '';
    position: absolute;
    left: var(--now-pos, 33.3%);
    right: 0;
    top: 0;
    height: 100%;
    background-image: linear-gradient(90deg, rgba(250, 70, 22, 0.4) 50%, transparent 50%);
    background-size: 6px 2px;
    background-repeat: repeat-x;
  }
  .radar-timeline-line::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: var(--progress, 0%);
    background: linear-gradient(90deg, rgba(250, 70, 22, 0.4), var(--orange));
    transition: width 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .radar-timeline-ticks {
    position: relative;
    height: 100%;
    width: 100%;
  }
  .radar-tick-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    position: absolute;
    top: 0;
    left: var(--pos, 50%);
    transform: translateX(-50%);
    padding-top: 4px;
  }
  .radar-tick {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: var(--surface-4);
    border: 2px solid var(--bg-secondary);
    box-sizing: content-box;
    transition: all 0.2s ease;
  }
  .radar-tick-wrap:hover .radar-tick {
    background: var(--text-tertiary);
    transform: scale(1.15);
  }
  .radar-tick-wrap.active .radar-tick {
    background: var(--orange);
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.25), 0 0 10px rgba(250, 70, 22, 0.5);
    transform: scale(1.2);
  }
  .radar-tick-wrap.now .radar-tick {
    background: var(--lightning-clear);
  }
  .radar-tick-wrap.active.now .radar-tick {
    background: var(--orange);
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.25), 0 0 10px rgba(250, 70, 22, 0.5);
  }
  .radar-tick-label {
    position: absolute;
    top: 20px;
    font-size: 11px;
    font-weight: 800;
    color: var(--text-muted);
    letter-spacing: 0.5px;
    white-space: nowrap;
    transition: color 0.2s ease;
  }
  .radar-tick-wrap.active .radar-tick-label {
    color: var(--orange);
  }
  .radar-tick-wrap.now .radar-tick-label {
    color: var(--lightning-clear);
  }
  .radar-tick-wrap.active.now .radar-tick-label {
    color: var(--orange);
  }
  .radar-tick-wrap.future .radar-tick {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px rgba(250, 70, 22, 0.55);
  }
  .radar-tick-wrap.future .radar-tick-label {
    color: var(--text-tertiary);
  }
  .radar-tick-wrap.future:hover .radar-tick {
    background: rgba(250, 70, 22, 0.15);
    box-shadow: inset 0 0 0 1.5px var(--orange);
  }
  .radar-tick-wrap.active.future .radar-tick {
    background: var(--orange);
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.25), 0 0 10px rgba(250, 70, 22, 0.5);
  }
  .radar-tick-wrap.active.future .radar-tick-label {
    color: var(--orange);
  }
  .radar-now-divider {
    position: absolute;
    top: 50%;
    left: var(--now-pos, 33.3%);
    transform: translate(-50%, -50%);
    width: 2px;
    height: 22px;
    background: var(--lightning-clear);
    border-radius: 1px;
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
    pointer-events: none;
    z-index: 2;
  }
  .radar-now-divider::before {
    content: 'NOW';
    position: absolute;
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    font-size: 8px;
    font-weight: 800;
    color: var(--lightning-clear);
    letter-spacing: 0.8px;
    white-space: nowrap;
  }
  /* Mobile: reduced, evenly-spaced, labeled tap stops. The .mobile class only
     renders on phones, so these can live outside a media query. */
  .radar-timeline-ticks.mobile {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .radar-timeline-ticks.mobile .radar-tick-wrap {
    position: static;
    transform: none;
    /* Shrink-to-fit equal columns so the ticks never force the timeline (and its
       grid neighbor) wider than the viewport. */
    flex: 1 1 0;
    min-width: 0;
    padding: 8px 2px 0;
  }
  .radar-timeline-ticks.mobile .radar-tick {
    width: 15px;
    height: 15px;
  }
  .radar-timeline-ticks.mobile .radar-tick-label {
    position: static;
    top: auto;
    margin-top: 8px;
    display: block;
    font-size: 11px;
  }
  .radar-timeline-time {
    font-size: 14px;
    font-weight: 800;
    color: var(--text-secondary);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.3px;
    white-space: nowrap;
    min-width: 72px;
    text-align: right;
  }
  .radar-timeline-time.is-now {
    color: var(--lightning-clear);
  }
  .radar-timeline-time.is-future {
    color: var(--orange);
  }
  .radar-forecast-badge {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translate(-50%, -8px);
    background: rgba(250, 70, 22, 0.18);
    border: 1px solid rgba(250, 70, 22, 0.55);
    color: var(--orange);
    padding: 5px 12px 5px 10px;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    z-index: 5;
    display: none;
    align-items: center;
    gap: 6px;
    backdrop-filter: blur(6px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    white-space: nowrap;
  }
  .radar-forecast-badge.active {
    display: inline-flex;
  }
  .radar-forecast-badge svg {
    width: 11px;
    height: 11px;
  }
  .radar-forecast-badge .fc-lead {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 800;
  }
  /* Geo-anchored venue label rendered by Leaflet (moves with the map). */
  .leaflet-tooltip.radar-venue-tip {
    background: rgba(0, 0, 0, 0.72);
    color: var(--orange);
    border: none;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }
  .leaflet-tooltip.radar-venue-tip::before {
    display: none;
  }
  /* Storm-cell marker: severity-colored dot with a lightning glyph. */
  .wc-storm-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35), 0 1px 4px rgba(0, 0, 0, 0.55);
  }
  .wc-storm-dot.sev-rotating {
    background: #ff3b30;
    animation: wc-storm-pulse 1.4s infinite;
  }
  .wc-storm-dot.sev-hail {
    background: #ffb020;
  }
  .wc-storm-dot.sev-general {
    background: #ffd60a;
  }
  .wc-storm-glyph {
    display: flex;
  }
  .wc-storm-glyph svg {
    width: 13px;
    height: 13px;
    display: block;
  }
  @keyframes wc-storm-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 2px rgba(255, 59, 48, 0.55), 0 0 0 0 rgba(255, 59, 48, 0.5);
    }
    50% {
      box-shadow: 0 0 0 2px rgba(255, 59, 48, 0.35), 0 0 0 12px rgba(255, 59, 48, 0);
    }
  }
  /* Motion arrowhead — a north-pointing triangle rotated to the travel bearing. */
  .wc-storm-arrow-head {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 11px solid #fff;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.5));
  }
  .leaflet-tooltip.radar-storm-tip {
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
  }
  .leaflet-tooltip.radar-storm-tip::before {
    display: none;
  }
  .radar-timestamp {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.7);
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    backdrop-filter: blur(4px);
    z-index: 5;
  }
  .radar-timestamp .live-dot-sm {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    margin-right: 5px;
    vertical-align: middle;
    animation: wc-pulse-sm 2s infinite;
  }
  @keyframes wc-pulse-sm {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
  .radar-zoom {
    position: absolute;
    bottom: 12px;
    left: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 5;
  }
  .radar-zoom-btn {
    width: 32px;
    height: 32px;
    background: rgba(0, 0, 0, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: white;
    border-radius: 6px;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }
  .radar-zoom-btn:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  .radar-legend {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.75);
    border: 1px solid var(--map-fill-2);
    border-radius: 8px;
    padding: 10px 12px;
    backdrop-filter: blur(6px);
    z-index: 5;
    max-width: 200px;
  }
  .radar-legend-title {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: var(--text-muted);
    font-weight: 600;
    margin-bottom: 6px;
  }
  .radar-legend-items {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .radar-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: var(--text-secondary);
  }
  .radar-legend-swatch {
    width: 16px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .radar-scale {
    position: absolute;
    bottom: 12px;
    left: 56px;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 5;
  }
  .radar-scale-bar {
    width: 60px;
    height: 2px;
    background: rgba(255, 255, 255, 0.4);
    position: relative;
  }
  .radar-scale-bar::before,
  .radar-scale-bar::after {
    content: '';
    position: absolute;
    top: -3px;
    width: 2px;
    height: 8px;
    background: rgba(255, 255, 255, 0.4);
  }
  .radar-scale-bar::before {
    left: 0;
  }
  .radar-scale-bar::after {
    right: 0;
  }
  .radar-scale-label {
    font-size: 9px;
    color: var(--text-muted);
    font-weight: 500;
  }

  /* ---------- Lightning / storms panels ---------- */
  .status-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 14px;
  }
  .status-banner.caution {
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.25);
  }
  .status-banner.clear {
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.25);
  }
  .status-banner.danger {
    background: rgba(255, 23, 68, 0.1);
    border: 1px solid rgba(255, 23, 68, 0.25);
  }
  .status-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .status-banner.caution .status-dot {
    background: var(--lightning-caution);
    box-shadow: 0 0 8px rgba(255, 152, 0, 0.5);
    animation: wc-pulse-caution 2s infinite;
  }
  .status-banner.clear .status-dot {
    background: var(--lightning-clear);
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
  }
  .status-banner.danger .status-dot {
    background: var(--lightning-danger);
    box-shadow: 0 0 8px rgba(255, 23, 68, 0.5);
    animation: wc-pulse-danger 1.5s infinite;
  }
  @keyframes wc-pulse {
    0%,
    100% {
      opacity: 1;
      box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.5);
    }
    50% {
      opacity: 0.7;
      box-shadow: 0 0 0 6px rgba(33, 150, 243, 0);
    }
  }
  @keyframes wc-pulse-caution {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.5);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(255, 152, 0, 0);
    }
  }
  @keyframes wc-pulse-danger {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(255, 23, 68, 0.6);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(255, 23, 68, 0);
    }
  }
  .status-text {
    font-weight: 700;
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .status-banner.caution .status-text {
    color: var(--lightning-caution);
  }
  .status-banner.clear .status-text {
    color: var(--lightning-clear);
  }
  .status-banner.danger .status-text {
    color: var(--lightning-danger);
  }
  .status-sub {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }
  .lightning-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .lightning-stat {
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px 8px;
    text-align: center;
  }
  .lightning-stat-value {
    font-weight: var(--fw-heavy);
    font-size: 26px;
    color: var(--text-primary);
    line-height: 1;
    margin-bottom: 6px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
  }
  .lightning-stat-value .unit {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .lightning-stat-label {
    font-size: 10px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 700;
  }
  .storm-cell-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .storm-cell-item {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
  }
  .storm-severity-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .storm-severity-dot.general {
    background: var(--lightning-clear);
  }
  .storm-severity-dot.hail {
    background: var(--lightning-caution);
  }
  .storm-severity-dot.rotating {
    background: var(--alert-severe);
  }
  .storm-cell-info {
    flex: 1;
  }
  .storm-cell-type {
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .storm-cell-detail {
    font-size: 11px;
    color: var(--text-muted);
  }
  .storm-cell-eta {
    font-weight: 700;
    font-size: 15px;
    color: var(--text-secondary);
    text-align: right;
  }
  .storm-cell-eta .eta-label {
    font-size: 9px;
    color: var(--text-muted);
    font-weight: 500;
    display: block;
  }

  /* ---------- Row 3: precip tiles ---------- */
  /* Same fluid 4-column grid as .row-top so the Precipitation card sits under
     Current Conditions and the three cards to its right line up under the radar
     card. The left column is a slightly heavier 1.2fr. Responsive rules below
     override on smaller screens. */
  .precip-row {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
  .tile-card {
    padding: 16px;
    display: flex;
    flex-direction: column;
  }
  .lightning-compact-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
  }
  .lightning-compact-status.caution {
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.25);
  }
  .lightning-compact-status .lc-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--lightning-caution);
    box-shadow: 0 0 6px rgba(255, 152, 0, 0.5);
    animation: wc-pulse-caution 2s infinite;
    flex-shrink: 0;
  }
  .lightning-compact-status .lc-text {
    font-weight: 800;
    font-size: 16px;
    text-transform: uppercase;
    color: var(--lightning-caution);
    letter-spacing: 0.5px;
  }
  .lightning-compact-status .lc-sub {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
    text-transform: none;
  }
  .lightning-mini-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .lightning-mini-stat {
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 8px 6px;
    text-align: center;
  }
  .lightning-mini-stat .lms-val {
    font-weight: var(--fw-heavy);
    font-size: 22px;
    line-height: 1;
    margin-bottom: 4px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
  }
  .lightning-mini-stat .lms-label {
    font-size: 9px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
  }
  .card-key {
    margin-top: 11px;
    padding-top: 11px;
    border-top: 1px solid var(--border);
  }
  .card-key-title {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-muted);
    margin-bottom: 8px;
  }
  .key-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .key-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 4px 9px;
    white-space: nowrap;
  }
  .key-pill .k-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .key-pill .k-bolt {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }
  .k-hail {
    background: var(--lightning-caution);
  }
  .k-storm {
    background: var(--lightning-clear);
  }
  .k-rotating {
    background: var(--alert-severe);
  }
  .radar-hint {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding: 9px 11px;
    border-radius: 8px;
    background: rgba(250, 70, 22, 0.08);
    border: 1px solid rgba(250, 70, 22, 0.22);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }
  .radar-hint:hover {
    background: rgba(250, 70, 22, 0.13);
    border-color: rgba(250, 70, 22, 0.42);
  }
  .radar-hint .rh-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .radar-hint .rh-icon svg {
    width: 15px;
    height: 15px;
    color: var(--orange);
  }
  .radar-hint-text {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.35;
    flex: 1;
    min-width: 0;
  }
  .radar-hint-cta {
    font-size: 11px;
    font-weight: 700;
    color: var(--orange);
    white-space: nowrap;
  }
  .radar-hint.on-alert {
    background: rgba(0, 0, 0, 0.16);
    border-color: rgba(255, 255, 255, 0.22);
  }
  .radar-hint.on-alert:hover {
    background: rgba(0, 0, 0, 0.24);
    border-color: rgba(255, 255, 255, 0.38);
  }
  .radar-hint.on-alert .rh-icon {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.3);
  }
  .radar-hint.on-alert .rh-icon svg {
    color: #fff;
  }
  .radar-hint.on-alert .radar-hint-text {
    color: rgba(255, 255, 255, 0.92);
  }
  .radar-hint.on-alert .radar-hint-cta {
    color: #fff;
  }
  .storm-compact-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 6px;
  }
  .storm-compact-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .storm-compact-item .sc-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .storm-compact-item .sc-dot.hail {
    background: var(--lightning-caution);
  }
  .storm-compact-item .sc-dot.general {
    background: var(--lightning-clear);
  }
  .storm-compact-item .sc-dot.rotating {
    background: var(--alert-severe);
  }
  .storm-compact-item .sc-info {
    flex: 1;
  }
  .storm-compact-item .sc-type {
    font-weight: 800;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .storm-compact-item .sc-detail {
    font-size: 11px;
    color: var(--text-muted);
    font-weight: 500;
  }
  .storm-compact-item .sc-eta {
    font-weight: 800;
    font-size: 15px;
    color: var(--text-secondary);
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .precip-compact-card {
    padding: 16px;
  }
  .precip-totals {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }
  .precip-total-box {
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px 12px;
    text-align: center;
  }
  .precip-total-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: var(--text-muted);
    margin-bottom: 8px;
    font-weight: 700;
  }
  .precip-total-value {
    font-weight: var(--fw-heavy);
    font-size: 38px;
    line-height: 1;
    color: var(--rain-moderate);
    font-variant-numeric: tabular-nums;
    letter-spacing: -1px;
  }
  .precip-total-value .unit {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-left: 4px;
  }
  .precip-rate-compact {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    background: rgba(33, 150, 243, 0.08);
    border: 1px solid rgba(33, 150, 243, 0.2);
    border-radius: 6px;
  }
  .precip-rate-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--rain-moderate);
    animation: wc-pulse 2s infinite;
    flex-shrink: 0;
  }
  .precip-rate-compact .rate-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
  }
  .precip-rate-compact .rate-value {
    font-weight: 800;
    font-size: 17px;
    color: var(--rain-moderate);
    margin-left: auto;
    font-variant-numeric: tabular-nums;
  }
  .next-rain-compact {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--surface-1);
    border-radius: 6px;
    margin-bottom: 10px;
  }
  .next-rain-compact .nr-label {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 700;
  }
  .next-rain-compact .nr-time {
    font-weight: 800;
    font-size: 17px;
  }
  .precip-chart-compact {
    position: relative;
    height: 120px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .precip-chart-compact::before {
    content: '0.25 in/hr';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 50%;
    border-top: 1px dashed var(--dash-line);
    font-size: 9px;
    color: var(--text-muted);
    padding-top: 2px;
    z-index: 1;
  }
  .precip-time-row {
    display: flex;
    margin-top: 8px;
    border-top: 1px solid var(--border);
    padding-top: 6px;
  }
  .precip-time-slot {
    flex: 1;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
  }
  .precip-time-slot .pt-ampm {
    font-size: 9px;
    font-weight: 500;
    color: var(--text-muted);
    margin-left: 1px;
  }
  .timeline-legend-compact {
    display: flex;
    gap: 12px;
    margin-top: 12px;
    flex-wrap: wrap;
    padding: 10px 12px;
    background: var(--surface-1);
    border-radius: 6px;
  }
  .legend-item-sm {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  .legend-swatch-sm {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
  .tile-card .next-rain-compact {
    margin-top: 10px;
  }

  /* ---------- Phrases trigger / modal ---------- */
  .phrases-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    margin-bottom: 4px;
    padding: 8px 10px;
    background: rgba(250, 70, 22, 0.06);
    border: 1px solid rgba(250, 70, 22, 0.15);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .phrases-trigger:hover {
    background: rgba(250, 70, 22, 0.1);
    border-color: rgba(250, 70, 22, 0.25);
  }
  .phrases-trigger > svg:not(.phrases-chevron) {
    width: 28px;
    height: 28px;
    padding: 6px;
    border-radius: 8px;
    color: var(--orange);
    flex-shrink: 0;
  }
  .phrases-trigger-text {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  .phrases-trigger .phrases-chevron {
    margin-left: auto;
    color: var(--orange);
    font-size: 14px;
    flex-shrink: 0;
  }
  .phrases-modal-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 500;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
  }
  .phrases-modal-overlay.active {
    display: flex;
  }
  .phrases-modal {
    background: var(--bg-primary);
    border: 1px solid var(--border-strong);
    border-radius: 12px;
    padding: 24px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
  }
  .phrases-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .phrases-modal-title {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .phrases-modal-title svg {
    width: 18px;
    height: 18px;
    color: var(--orange);
  }
  .phrases-modal-close {
    background: var(--surface-2);
    border: 1px solid var(--hairline);
    color: var(--text-muted);
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;
  }
  .phrases-modal-close svg {
    width: 14px;
    height: 14px;
  }
  .phrases-modal-close:hover {
    background: var(--surface-3);
    color: var(--text-primary);
  }
  .phrases-modal-body {
    font-size: 15px;
    line-height: 1.7;
    color: var(--text-secondary);
  }
  .phrases-modal-source {
    font-size: 10px;
    color: var(--text-muted);
    margin-top: 14px;
  }

  /* ---------- Day summary popup ---------- */
  .day-card {
    position: relative;
  }
  .day-summary-icon {
    position: absolute;
    top: 8px;
    right: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    cursor: pointer;
  }
  .day-summary-icon svg {
    width: 12px;
    height: 12px;
    color: var(--orange);
  }
  .day-summary-icon:hover {
    border-color: rgba(250, 70, 22, 0.3);
    background: rgba(250, 70, 22, 0.1);
  }
  .day-summary-popup {
    display: none;
    align-items: flex-start;
    gap: 10px;
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-strong);
    border-radius: 9px;
    padding: 11px 13px;
    font-size: 12px;
    font-weight: 400;
    color: var(--text-secondary);
    line-height: 1.5;
    width: 232px;
    text-align: left;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.6);
    z-index: 200;
    pointer-events: none;
    text-transform: none;
    letter-spacing: 0;
  }
  .day-summary-popup::before {
    content: '';
    flex-shrink: 0;
    margin-top: 1px;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: rgba(250, 70, 22, 0.16)
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fa4616' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'/%3E%3C/svg%3E")
      center / 15px no-repeat;
    border: 1px solid rgba(250, 70, 22, 0.32);
  }
  .day-summary-popup::after {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 8px;
    border: 6px solid transparent;
    border-bottom-color: var(--bg-secondary);
  }
  .day-card:nth-child(-n + 3) .day-summary-popup {
    left: 0;
    right: auto;
  }
  .day-card:nth-child(-n + 3) .day-summary-popup::after {
    left: 8px;
    right: auto;
  }
  .day-summary-icon:hover .day-summary-popup {
    display: flex;
  }

  /* ---------- Row 4: forecast ---------- */
  .forecast-card {
    margin-bottom: 16px;
  }
  .forecast-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
  }
  .day-card {
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 18px 12px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .day-card:hover {
    background: var(--surface-2);
    border-color: var(--border-hover);
  }
  .day-card.selected {
    border-color: var(--brand-primary);
    border-width: 2px;
    background: rgba(250, 70, 22, 0.08);
    box-shadow: var(--shadow-glow), inset 0 0 20px rgba(250, 70, 22, 0.04);
  }
  @keyframes wc-fadeSlideIn {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  .day-name {
    font-weight: var(--fw-heavy);
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 4px;
  }
  .day-date {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    margin-bottom: 12px;
  }
  .day-icon {
    width: 52px;
    height: 52px;
    margin: 0 auto 10px;
  }
  .day-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .day-temps {
    font-size: 24px;
    font-weight: var(--fw-heavy);
    margin-bottom: 8px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
  }
  .day-temps .low {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 18px;
  }
  .day-precip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    color: var(--rain-light);
    margin-bottom: 4px;
  }
  .day-precip-icon {
    width: 13px;
    height: 13px;
    fill: var(--rain-light);
  }
  .day-rain-total {
    font-size: 14px;
    font-weight: 700;
    color: var(--rain-moderate);
  }
  .day-wind {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    margin-top: 4px;
  }
  .forecast-range-chips {
    display: flex;
    gap: 6px;
    margin-left: auto;
  }
  .forecast-chip {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    padding: 8px 16px;
    border-radius: 20px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
  }
  .forecast-chip.active {
    background: rgba(250, 70, 22, 0.15);
    border-color: rgba(250, 70, 22, 0.4);
    color: var(--orange);
  }
  .forecast-chip:hover {
    border-color: var(--border-hover);
    color: var(--text-secondary);
  }
  .forecast-chip.active:hover {
    background: rgba(250, 70, 22, 0.22);
    border-color: rgba(250, 70, 22, 0.5);
    color: var(--orange);
  }

  /* ---------- Modal ---------- */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(4px);
    z-index: 999;
    display: none;
    align-items: center;
    justify-content: center;
  }
  .modal-overlay.active {
    display: flex;
  }
  /* Current Conditions stat tiles: 4 across on desktop, 2 on mobile so they
     never overflow the modal and cause horizontal scroll. */
  .cond-stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  @media (max-width: 560px) {
    .cond-stat-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  /* Other modal stat grids that would otherwise overflow narrow screens. */
  .precip-stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  .lightning-stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  .lightning-protocol-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  @media (max-width: 560px) {
    .precip-stat-grid {
      grid-template-columns: 1fr;
    }
    .lightning-stat-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .lightning-protocol-grid {
      grid-template-columns: 1fr;
    }
  }
  .modal-content {
    background: var(--bg-modal);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    width: 90vw;
    max-width: 960px;
    max-height: 85vh;
    overflow-y: auto;
    padding: 24px;
    box-shadow: var(--shadow-xl);
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb) transparent;
  }
  .modal-content::-webkit-scrollbar {
    width: 6px;
  }
  .modal-content::-webkit-scrollbar-track {
    background: transparent;
  }
  .modal-content::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb);
    border-radius: 3px;
  }
  .modal-content::-webkit-scrollbar-thumb:hover {
    background: var(--scrollbar-thumb-hover);
  }
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  .modal-title {
    font-size: 22px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }
  .modal-title-icon-tile {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .modal-title-icon-tile svg {
    width: 22px;
    height: 22px;
    color: var(--brand-primary);
    stroke-width: 2.2;
  }
  .modal-title-text {
    min-width: 0;
  }
  .modal-close {
    background: var(--surface-3);
    border: 1px solid var(--border);
    color: var(--text-primary);
    width: 38px;
    height: 38px;
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
  }
  .modal-close svg {
    width: 18px;
    height: 18px;
  }
  .modal-close:hover {
    background: var(--surface-3);
  }
  .modal-overlay.fullscreen .modal-content {
    width: 98vw;
    max-width: 98vw;
    height: 94vh;
    max-height: 94vh;
    border-radius: 10px;
  }
  .modal-overlay.fullscreen .modal-content .radar-map-wrap {
    min-height: 0;
    flex: 1;
    height: calc(94vh - 160px);
  }
  .hourly-table {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .hourly-table table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
  }
  .hourly-table th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-muted);
    font-weight: 600;
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }
  .hourly-table td {
    font-size: 13px;
    padding: 9px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text-secondary);
  }
  .hourly-table tr:last-child td {
    border-bottom: none;
  }
  .hourly-table .hour-icon {
    width: 28px;
    height: 28px;
    vertical-align: middle;
  }
  .hourly-table .hour-icon img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  .hourly-table .rain-cell {
    font-weight: 600;
  }

  /* ---------- Legend tooltips (modal) ---------- */
  .stat-label-tip {
    position: relative;
    cursor: help;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .stat-label-tip .tip-icon {
    width: 13px;
    height: 13px;
    font-size: 8px;
  }
  .stat-tip {
    display: none;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(100% + 10px);
    min-width: 220px;
    max-width: 300px;
    background: var(--bg-primary);
    border: 1px solid var(--border-strong);
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--text-secondary);
    z-index: 30;
    pointer-events: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
    text-transform: none;
    letter-spacing: 0;
    text-align: left;
    white-space: normal;
  }
  .stat-tip strong {
    color: var(--text-primary);
    font-weight: 600;
  }
  .stat-label-tip:hover .stat-tip {
    display: block;
  }
  .stat-tip.tip-down {
    bottom: auto;
    top: calc(100% + 10px);
  }
  .legend-row-label {
    position: relative;
    font-weight: 700;
    font-size: 16px;
    color: var(--text-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .tip-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid var(--text-muted);
    font-size: 10px;
    line-height: 1;
    font-weight: 700;
    color: var(--text-muted);
    flex-shrink: 0;
    transition: all 0.2s ease;
  }
  .legend-row-label:hover .tip-icon {
    border-color: var(--text-secondary);
    color: var(--text-secondary);
  }
  .legend-tip {
    display: none;
    position: absolute;
    left: 0;
    bottom: calc(100% + 12px);
    min-width: 300px;
    max-width: 380px;
    background: var(--bg-primary);
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    padding: 16px 18px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.55;
    color: var(--text-secondary);
    z-index: 20;
    pointer-events: none;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
  }
  .legend-tip strong {
    color: var(--text-primary);
    font-weight: 600;
  }
  .legend-row-label:hover .legend-tip {
    display: block;
  }
  .legend-bolt {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
  .legend-swatch-outline {
    width: 22px;
    height: 15px;
    border-radius: 3px;
    border-width: 2.5px;
    border-style: solid;
    background: transparent;
    flex-shrink: 0;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 1100px) {
    .row-top {
      grid-template-columns: 1fr;
    }
    .row-top .radar-hub {
      grid-column: auto;
    }
    .precip-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 860px) {
    .row-top {
      grid-template-columns: 1fr;
    }
    .precip-row {
      grid-template-columns: repeat(2, 1fr);
    }
    .lightning-stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .lightning-mini-stats {
      grid-template-columns: repeat(2, 1fr);
    }
    .forecast-days {
      display: flex;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      gap: 8px;
      padding-bottom: 8px;
    }
    .day-card {
      min-width: 120px;
      flex-shrink: 0;
    }
    .forecast-range-chips {
      gap: 4px;
    }
    .forecast-chip {
      padding: 5px 10px;
      font-size: 11px;
    }
    .dashboard-header {
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 14px;
      padding: 16px;
    }
    .dashboard-header h1.dash-title {
      gap: 11px;
    }
    .header-attribution {
      align-items: flex-start;
    }
    .storm-compact-item .sc-eta {
      font-size: 12px;
    }
    .precip-row > div:nth-child(n + 3) {
      grid-column: 1 / -1;
    }
  }
  @media (max-width: 600px) {
    .dashboard {
      padding: 16px 12px 40px;
    }
    .dashboard-header {
      padding: 18px 16px;
      gap: 13px;
    }
    .dashboard-header-left {
      flex-direction: column;
      align-items: center;
      gap: 9px;
      width: 100%;
    }
    .header-icon-tile {
      display: none;
    }
    .dashboard-header-text {
      align-items: center;
      gap: 5px;
      width: 100%;
    }
    .dashboard-header h1.dash-title {
      gap: 10px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .pitpay-wordmark {
      height: 38px;
      margin-top: 0;
    }
    .title-stack {
      align-items: center;
      gap: 6px;
    }
    .dashboard-header h1 {
      font-size: 24px;
    }
    .dashboard-header .venue-name {
      font-size: 12.5px;
      justify-content: center;
      text-align: center;
      flex-wrap: wrap;
    }
    .venue-name .xweather-badge-sm {
      margin-left: 0;
      margin-top: 2px;
    }
    .header-attribution {
      width: 100%;
      align-items: center;
      border-top: 1px solid var(--border-default);
      padding-top: 14px;
    }
    .presented-by {
      align-items: center;
    }
    .presented-by-label {
      font-size: 9px;
      letter-spacing: 1.4px;
    }
    .presented-by-logo {
      padding: 8px 16px;
      border-radius: 6px;
    }
    .presented-by-logo img {
      height: 34px;
    }
    .precip-row {
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    .precip-total-value {
      font-size: 24px;
    }
    .precip-totals {
      gap: 6px;
    }
    .precip-total-box {
      padding: 10px 8px;
    }
    .precip-total-label {
      font-size: 9px;
    }
    .lightning-mini-stats {
      grid-template-columns: repeat(2, 1fr);
      gap: 4px;
    }
    .lightning-mini-stat {
      padding: 6px 4px;
    }
    .lightning-mini-stat .lms-val {
      font-size: 15px;
    }
    .lightning-compact-status {
      padding: 8px 10px;
    }
    .storm-compact-item {
      padding: 6px 8px;
      gap: 6px;
    }
    .storm-compact-item .sc-type {
      font-size: 10px;
    }
    .storm-compact-item .sc-detail {
      font-size: 9px;
    }
    .storm-compact-item .sc-eta {
      font-size: 11px;
    }
    .forecast-days {
      display: flex;
      overflow-x: auto;
      gap: 6px;
      padding-bottom: 6px;
    }
    .day-card {
      min-width: 110px;
      flex-shrink: 0;
    }
    .day-card .day-temps {
      font-size: 17px;
    }
    .day-card .day-name {
      font-size: 13px;
    }
    .forecast-range-chips {
      gap: 3px;
    }
    .forecast-chip {
      padding: 4px 8px;
      font-size: 10px;
      letter-spacing: 0.5px;
    }
    .card-title-row {
      flex-wrap: wrap;
      gap: 8px;
    }
    .alert-summary {
      display: none;
    }
    .radar-tabs {
      gap: 4px;
    }
    .radar-tab {
      padding: 6px 12px;
      font-size: 11px;
    }
    .layer-toggles {
      gap: 4px;
    }
    .layer-chip {
      padding: 4px 10px;
      font-size: 10px;
    }
    .modal-content {
      padding: 16px;
    }
    .modal-title {
      font-size: 15px;
    }
    .card-key {
      margin-top: 9px;
      padding-top: 9px;
    }
    .key-pill {
      font-size: 10.5px;
      padding: 4px 8px;
    }
    .radar-hint {
      padding: 8px 10px;
      gap: 8px;
    }
    .radar-hint .rh-icon {
      width: 26px;
      height: 26px;
    }
    .radar-hint-text {
      font-size: 11px;
    }
    .expand-btn {
      width: 40px;
      height: 40px;
    }
    .card-title-tip .tip-text {
      width: min(248px, 78vw);
    }
    .alert-type {
      font-size: 16px;
      letter-spacing: -0.2px;
    }
    .radar-legend {
      bottom: 8px;
      right: 8px;
      padding: 8px 10px;
    }
    .radar-legend-item {
      font-size: 9px;
    }
    .legend-tip {
      min-width: 0 !important;
      max-width: 90vw !important;
    }
    .stat-tip {
      min-width: 0 !important;
      max-width: 90vw !important;
    }
    .day-summary-popup {
      width: min(232px, 88vw);
    }
    .radar-tick-label {
      display: none;
    }
    /* The reduced mobile timeline uses evenly-spaced labeled ticks, so the
       continuous line, NOW divider, and right-side readout are dropped (they'd
       misalign) — the labeled NOW/future ticks convey the same thing. */
    .radar-timeline-line,
    .radar-now-divider,
    .radar-timeline-time {
      display: none;
    }
    .radar-timeline-track {
      height: auto;
      padding-top: 0;
    }
  }
  @media (max-width: 440px) {
    .precip-row {
      grid-template-columns: 1fr;
    }
    .lightning-mini-stats {
      grid-template-columns: repeat(2, 1fr);
    }
    .dashboard-header h1 {
      font-size: 22px;
    }
    .pitpay-wordmark {
      height: 35px;
    }
  }
  @media (max-width: 640px) {
    .modal-content {
      width: 94vw;
      padding: 16px;
    }
    .modal-header {
      margin-bottom: 16px;
    }
    .lz-hero {
      flex-wrap: wrap;
      gap: 14px !important;
      padding: 18px !important;
    }
    .lz-hero .lz-last {
      text-align: left !important;
      width: 100%;
      border-top: 1px solid var(--hairline);
      padding-top: 12px;
    }
    .modal-title {
      font-size: 16px;
    }
  }
`;
